import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { HeartPulse, ShieldCheck, MessageCircleHeart, Phone, BookHeart, Wind, ArrowRight, Sparkles } from "lucide-react";
import TrendBanner from "../components/TrendBanner";
import { useTranslation } from "react-i18next";

const QUICK = [
  { to: "/checkin", icon: HeartPulse, title: "Daily Check-in", desc: "Rate how you feel right now", color: "bg-teal-50 text-teal-900", id: "quick-checkin" },
  { to: "/plan", icon: ShieldCheck, title: "My Safety Plan", desc: "Your 7-step safety plan", color: "bg-amber-50 text-amber-900", id: "quick-plan" },
  { to: "/assistant", icon: MessageCircleHeart, title: "AI Companion", desc: "Walk through your plan together", color: "bg-slate-100 text-slate-900", id: "quick-assistant" },
  { to: "/contacts", icon: Phone, title: "Crisis Contacts", desc: "NHS 111, Samaritans, SHOUT", color: "bg-rose-50 text-rose-900", id: "quick-contacts" },
  { to: "/mood", icon: BookHeart, title: "Mood Diary", desc: "Track patterns over time", color: "bg-blue-50 text-blue-900", id: "quick-mood" },
  { to: "/toolkit", icon: Wind, title: "Coping Toolkit", desc: "Box breathing · 5-4-3-2-1", color: "bg-emerald-50 text-emerald-900", id: "quick-toolkit" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [latest, setLatest] = useState(null);
  const [vault, setVault] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [c, v] = await Promise.all([api.get("/checkins"), api.get("/vault")]);
        setLatest(c.data[0] || null);
        setVault(v.data);
      } catch (err) {
        // Non-fatal — dashboard renders without the latest-checkin card / vault snippet.
        console.error("Dashboard initial load failed", err);
      }
    })();
  }, []);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return t("dashboard.morning");
    if (h < 18) return t("dashboard.afternoon");
    return t("dashboard.evening");
  };

  return (
    <div className="space-y-6 fade-up" data-testid="dashboard-page">
      <header>
        <div className="text-sm text-slate-500">{new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mt-1">
          {greeting()}, {user?.name?.split(" ")[0] || "friend"}.
        </h1>
        <p className="text-slate-600 mt-2 max-w-xl">
          This space helps you stay safe long enough to access support. Start with a quick check-in, or open your safety plan.
        </p>
      </header>

      <TrendBanner />

      {latest && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex items-center justify-between" data-testid="latest-checkin-card">
          <div>
            <div className="text-xs text-slate-500">Your last check-in</div>
            <div className="font-display text-xl font-bold text-slate-900 mt-0.5">Distress {latest.distress} / 10</div>
            <div className="text-xs text-slate-500 mt-1">{new Date(latest.created_at).toLocaleString("en-GB")}</div>
          </div>
          <Link to="/checkin" data-testid="latest-checkin-cta" className="text-teal-700 font-medium text-sm flex items-center gap-1 hover:gap-2 transition-all">
            New check-in <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {QUICK.map((q) => (
          <Link
            key={q.to}
            to={q.to}
            data-testid={q.id}
            className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:border-teal-400 transition-colors group"
          >
            <div className={`w-11 h-11 rounded-xl ${q.color} flex items-center justify-center mb-3`}>
              <q.icon className="w-5 h-5" />
            </div>
            <div className="font-display text-lg font-semibold text-slate-900">{q.title}</div>
            <div className="text-sm text-slate-500 mt-1">{q.desc}</div>
            <div className="text-teal-700 text-sm font-medium mt-3 flex items-center gap-1 group-hover:gap-2 transition-all">
              Open <ArrowRight className="w-4 h-4" />
            </div>
          </Link>
        ))}
      </div>

      {vault.length > 0 && (
        <div className="bg-gradient-to-br from-teal-50 to-amber-50 rounded-2xl p-6 border border-teal-100" data-testid="reasons-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-teal-700" />
            <h2 className="font-display text-lg font-bold text-slate-900">A reason for today</h2>
          </div>
          <p className="text-slate-700 text-lg leading-relaxed italic">
            "{vault[Math.floor(Math.random() * vault.length)].content}"
          </p>
          <Link to="/vault" data-testid="open-vault" className="text-teal-700 text-sm font-medium mt-3 inline-flex items-center gap-1 hover:gap-2 transition-all">
            Open your safety vault <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
        SafeHaven supports your safety plan. It does not replace clinicians, crisis teams, or emergency services. If you are in immediate danger call <strong>999</strong>.
      </div>
    </div>
  );
}
